/**
 * useSuppliers — ViewModel for /manage/suppliers.
 *
 * Per SOP 02 §"MVVM Pattern" + §"List Pagination". Composes
 * usePaginatedList for reads; owns CRUD mutations.
 *
 * Mirrors src/viewmodels/useCategories.ts in shape minus the M:N
 * picker (suppliers don't pick anything; raw materials pick them).
 */

import { useCallback } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES, fillPath } from '../constants/routes.constants'
import type { SupplierDto, SupplierType } from '../models/supplier.model'
import { mutateJson } from '../utils/api/mutateJson'
import { parseApiError } from '../utils/api/parseApiError'
import { usePaginatedList } from './usePaginatedList'

// Single-source-of-truth: src/models/supplier.model.ts.
// Per SOP 02 §"Mutation Patterns (LOCKED)" — DTO source rule.
export type { SupplierDto, SupplierType } from '../models/supplier.model'

export interface CreateSupplierInput {
  readonly name: string
  readonly description: string | null
  readonly contact_info: string | null
  readonly supplier_type: SupplierType
}

export interface UpdateSupplierInput {
  readonly name?: string
  readonly description?: string | null
  readonly contact_info?: string | null
  readonly supplier_type?: SupplierType
}

export interface UseSuppliersReturn {
  readonly items: SupplierDto[]
  readonly total: number
  readonly page: number
  readonly pageSize: number
  readonly isLoading: boolean
  readonly error: string | null
  readonly setPage: (n: number) => void
  readonly reload: () => void
  readonly create: (
    input: CreateSupplierInput
  ) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
  readonly update: (
    id: string,
    patch: UpdateSupplierInput
  ) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
  readonly remove: (id: string) => Promise<{ ok: true } | { ok: false; message: string }>
}

interface ListBody {
  readonly success: true
  readonly data: {
    readonly items: SupplierDto[]
    readonly total: number
    readonly page: number
    readonly pageSize: number
  }
}

export function useSuppliers(): UseSuppliersReturn {
  const { authedFetch } = useAuth()

  const paged = usePaginatedList<SupplierDto, undefined>({
    fetcher: useCallback(
      async (page, pageSize, signal) => {
        const url = `${APIROUTES.MANAGESUPPLIERS}?page=${page}&pageSize=${pageSize}`
        const res = await authedFetch(url, { signal })
        if (!res.ok) {
          const { message } = await parseApiError(res)
          throw new Error(message)
        }
        const body = (await res.json()) as ListBody
        return { items: body.data.items, total: body.data.total, extras: undefined }
      },
      [authedFetch]
    )
  })

  const create = useCallback(
    async (input: CreateSupplierInput) => {
      const r = await mutateJson<SupplierDto>(
        authedFetch,
        APIROUTES.MANAGESUPPLIERS,
        'POST',
        input
      )
      if (!r.ok) return r
      if (paged.page === 1) paged.insertItem(r.data)
      else paged.reload()
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  const update = useCallback(
    async (id: string, patch: UpdateSupplierInput) => {
      const r = await mutateJson<SupplierDto>(
        authedFetch,
        fillPath(APIROUTES.MANAGESUPPLIERITEM, { id }),
        'PATCH',
        patch
      )
      if (!r.ok) return r
      paged.updateItem((it) => it.id === id, r.data)
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  const remove = useCallback(
    async (id: string) => {
      const r = await mutateJson<{ ok: true }>(
        authedFetch,
        fillPath(APIROUTES.MANAGESUPPLIERITEM, { id }),
        'DELETE'
      )
      if (!r.ok) return { ok: false as const, message: r.message }
      paged.removeItem((it) => it.id === id)
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  return {
    items: paged.items,
    total: paged.total,
    page: paged.page,
    pageSize: paged.pageSize,
    isLoading: paged.isLoading,
    error: paged.error,
    setPage: paged.setPage,
    reload: paged.reload,
    create,
    update,
    remove
  }
}
